import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import { HourglassIcon } from '@/components/game/GameIcons'
import { remainingLabel, seasonClock } from '@/lib/seasonClock'

/**
 * Le sablier de fin de saison, dans la barre globale.
 *
 * Il ne s'allume QUE dans la dernière quinzaine. Deux raisons, et les deux
 * comptent : c'est le seul préavis de reset du jeu (le runbook de la fiche 31
 * pose « J-14, le compte à rebours est en ligne, sinon on ne reset pas »), et la
 * barre est déjà à saturation le reste du temps, quatre commentaires
 * d'AppLayout documentant autant de débordements déjà corrigés sur la tranche
 * xl vers 2xl. Coût en densité les neuf dixièmes du temps : zéro.
 *
 * Avant ce lot, la date de fin de saison ne se lisait qu'en tête du Grand
 * Chantier, écran dont le lien est masqué sous le Quartier-Général 15 : six
 * comptes sur vingt et un la voyaient.
 *
 * Aucune requête : la saison active arrive avec le capitaine, dans la charge de
 * `/api/me`. Le compteur se dérive de ses dates et JAMAIS de
 * `SEASON_LENGTH_WEEKS`, qui ne décrit que la durée d'une saison neuve.
 */
export function SeasonCountdown() {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  // Fermeture au clic extérieur et à Escape (actifs seulement quand ouvert),
  // même contrat que StreakChip et la cloche.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const season = user?.season ?? null
  const clock = season ? seasonClock(season.startedAt, season.endsAt) : null
  if (!season || !clock || 'calm' === clock.urgency) return null

  const imminent = 'imminent' === clock.urgency
  const left = remainingLabel(clock.daysRemaining)
  const countdown = clock.daysRemaining > 0 ? `J-${clock.daysRemaining}` : 'J0'
  // « Fin de saison dans dernier jour » est ce que rendait la forme naïve : le
  // libellé se compose donc en deux branches, parce que c'est un lecteur d'écran
  // qui l'énonce et que « J0 » ne s'annonce pas.
  const spoken = clock.daysRemaining > 0 ? `Fin de saison dans ${left}` : 'Fin de saison, dernier jour'

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={spoken}
        title="Fin de saison"
        data-urgency={clock.urgency}
        // px-1 sur toute la tranche xl → 3xl, la plus serrée de la barre : quatre
        // pixels de moins de chaque côté, en plus du gap compacté du cluster.
        className={`relative flex items-center gap-1 h-9 px-1.5 xl:px-1 3xl:px-1.5 rounded-md transition-colors hover:bg-ocean-700/40 ${open ? 'bg-ocean-700/40' : ''}`}
      >
        <HourglassIcon
          className={
            imminent
              ? 'w-5 h-5 text-rum-100 drop-shadow-[0_0_6px_rgba(186,102,59,0.75)]'
              : 'w-5 h-5 text-sand-300 drop-shadow-[0_0_6px_rgba(229,204,143,0.55)]'
          }
        />
        {/* Le compte ne s'écrit inline QUE sur la tranche où la barre a de la
            place, et cette tranche est étroite : de `sm` à `xl`, c'est-à-dire là
            où la nav est repliée dans le burger. Partout ailleurs c'est un badge.
            Trois mesures, pas trois intuitions :
            - à 375 px avec le libellé inline, le cluster de droite faisait 205 px
              et la couronne chevauchait le logo de deux pixels ;
            - à 1280 px sur une barre complète (sept entrées de nav, ressources,
              sept emblèmes dont les Récits), la pilule de ressources passait
              par-dessus « Phare » de neuf pixels ;
            - de 1536 à 1759 px le conteneur plafonne à 1600 px alors que le nom du
              capitaine apparaît, donc cette tranche n'est PAS plus large que xl
              pour ce cluster. Y rendre le libellé était une erreur de
              raisonnement. Le palier 3xl (1760 px) est le premier qui ait
              réellement la place, et c'est là que le reste de la barre se
              desserre. Le sablier garde quand même son badge au-delà : il n'a
              rien à y regagner, et la règle ci-dessous tient toujours.
            La barre est le seul endroit du jeu où le préavis de reset est visible,
            donc le sablier y reste ; mais il est la dernière entrée arrivée, donc
            c'est à lui de se serrer. */}
        <span
          className={`hidden sm:inline xl:hidden text-sm tabular-nums ${imminent ? 'text-rum-100' : 'text-sand-200'}`}
        >
          {countdown}
        </span>
        <span
          data-testid="season-countdown-badge"
          className={`grid sm:hidden xl:grid absolute -bottom-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 place-items-center rounded-full bg-ocean-900 border text-[10px] leading-none tabular-nums ${
            imminent ? 'border-rum-100/60 text-rum-100' : 'border-sand-500/40 text-sand-200'
          }`}
          aria-hidden="true"
        >
          {clock.daysRemaining}
        </span>
      </button>

      {open && (
        <>
          {/* Fond mobile : l'en-tête flouté est le bloc conteneur des enfants
              `fixed`, donc une hauteur en dvh explicite et jamais un inset-0,
              qui se replierait sur la seule hauteur de la barre. */}
          <div
            className="fixed inset-x-0 top-16 h-[calc(100dvh-4rem)] z-30 bg-ocean-900/70 sm:hidden"
            aria-hidden="true"
            onMouseDown={() => setOpen(false)}
          />
          <div
            role="dialog"
            aria-label="Fin de saison"
            className="fixed left-2 right-2 top-[4.5rem] z-40 rounded-xl border border-sand-500/20 bg-ocean-900/95 backdrop-blur-md shadow-xl p-4 text-left sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-72"
          >
            <p className="font-display text-sand-100">{season.name}</p>
            {/* Deux branches, pour la même raison que le libellé accessible :
                « il reste dernier jour » est ce que rendait la forme unique. Le
                cas n'est pas théorique, aucun cron ne clôt une saison, donc
                cette phrase reste à l'écran toute la dernière journée. */}
            <p className="mt-1 text-sm text-ocean-100">
              Semaine <span className="tabular-nums text-sand-200">{clock.week}</span> sur{' '}
              <span className="tabular-nums text-sand-200">{clock.totalWeeks}</span>
              {clock.daysRemaining > 0 ? (
                <>
                  , il reste{' '}
                  <span className={`tabular-nums ${imminent ? 'text-rum-100' : 'text-sand-200'}`}>{left}</span>.
                </>
              ) : (
                <>
                  , et c'est le <span className={imminent ? 'text-rum-100' : 'text-sand-200'}>{left}</span>.
                </>
              )}
            </p>
            <div className="mt-3 h-1.5 rounded-full bg-ocean-700/60 overflow-hidden" aria-hidden="true">
              <div
                className="h-full rounded-full bg-gradient-to-r from-sand-500 to-sand-300"
                style={{ width: `${Math.round(clock.progress * 100)}%` }}
              />
            </div>
            <p className="mt-3 text-xs text-ocean-200 leading-relaxed">
              Au reset, ton île, ta flotte, tes bâtiments et tes objectifs de saison repartent de
              zéro. Ton compte, ton prestige, tes titres et ta cabine restent gravés.
            </p>
            {/* Le chapitre DÉDIÉ, pas le chapitre 01 : `#principe` tombait en tête
                du Codex, donc le lien promettait un bilan et rendait la table des
                matières. */}
            <Link
              to="/codex#fin-de-saison"
              onClick={() => setOpen(false)}
              className="mt-3 inline-block text-xs text-sand-300 underline underline-offset-4 hover:text-sand-100"
            >
              Ce que le reset emporte
            </Link>
          </div>
        </>
      )}
    </div>
  )
}
